// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import translate from "@insite/client-framework/Translate";
import siteMessage from "@insite/client-framework/SiteMessage";
import { css } from "styled-components";
import { fontConfig } from "@stoneage/XDL/constants";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { connect } from "react-redux";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { color } from "@stoneage/XDL/Utilities";

interface OwnProps {}

const mapStateToProps = (state: CustomAppState) => {
    const { search } = getLocation(state);
    const requestAccountPageLink = getPageLinkByPageType(state, "RequestAccountPage");
    return {
        createAccountUrl: requestAccountPageLink ? `${requestAccountPageLink.url}${search}` : undefined,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface MiniSignInStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    signInCreateNewAccountContainer?: ClickablePresentationProps;
    createAccountTitle?: TypographyPresentationProps;
    createAccountButton?: ButtonPresentationProps;
}

export const miniSignInStyles: MiniSignInStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    title: {
        variant: "h4",
    },
    description: {
        variant: "p",
        css: css`
            font-size: ${fontConfig.regularSingleLine.size}px;
            line-height: ${fontConfig.regularSingleLine.lineHeight};
            font-weight: ${fontConfig.weight.regular};
        `,
    },
    createAccountTitle: {
        variant: "h4",
        css: css`
            padding-top: 24px;
            margin-top: 24px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    createAccountButton: {
        variant: "secondary",
        css: css`
            align-self: baseline;
        `,
    },
};

const styles = miniSignInStyles;
const MiniSignIn: FC<Props> = ({ createAccountUrl }) => {
    const history = useHistory();

    const createNewAccountHandler = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="mini-signIn">
            <Typography {...styles.title}>{translate("Sign In")}</Typography>
            <Typography {...styles.description}>{siteMessage("Custom_SignInOverlay_WelcomeMsg")}</Typography>
            {createWidgetElement("SignIn/SignInExternalProviders", {
                fields: {
                    isMiniSignIn: true,
                },
            })}
            <Typography {...styles.createAccountTitle}>{translate("Request An Account")}</Typography>
            <Typography {...styles.description}>{siteMessage("Custom_SignInOverlay_RequestAccountMsg")}</Typography>
            <Button onClick={createNewAccountHandler} {...styles.createAccountButton}>
                {translate("Request An Account")}
            </Button>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(MiniSignIn);
